import { getIdsFromGeocoderResult, getMapboxGeocodeResult } from "@phc/common"
import type {
  AssetGroup,
  GeotagSchema as Geotag,
} from "@phc/common/gen/preferencesGen"
import { IndustryType } from "@phc/common/gen/preferencesGen"
import { MAPBOX_ACCESS_TOKEN } from "../../../../utils/env"
import { convertGeocoderResultToLocation } from "../../../../utils/helpers/assetHelper"
import type { AssetInput } from "../../../../utils/preferencesApi"
import type { AssetCSVRow } from "./assetSchema"

export const LOCAL_STORAGE_PREPARED_ASSETS_KEY = "preparedAssets"
export const BULK_ASSET_GROUP = "Bulk"

interface PreparedAsset {
  asset?: AssetInput
  csvAsset: AssetCSVRow
  error?: string
}

export const makeAssetFromCSVAsset = async (
  csvAsset: AssetCSVRow
): Promise<PreparedAsset> => {
  const existingAsset = findAssetInLocalStorage(csvAsset)
  if (existingAsset) {
    return { asset: existingAsset, error: undefined, csvAsset }
  }

  let csvAssetMapboxLocation
  try {
    csvAssetMapboxLocation = await reverseGeocodeToPHCLocation(csvAsset)
    if (!csvAssetMapboxLocation)
      throw new Error(
        `No matching location found for ${csvAsset.Name || "unnamed location"}`
      )
  } catch (err) {
    let error
    if (err instanceof Error) {
      error = err.message
    }
    return {
      csvAsset,
      error: error || (err as string),
    }
  }

  const newAsset: Partial<AssetInput> = {
    asset_id: "",
    name: csvAsset.Name,
    asset_types: [csvAsset.Type],
    base_event: {
      geotags: [csvAssetMapboxLocation],
    },
    asset_groups: getGroups(csvAsset),
    threat_radius: csvAsset["Threat Radius"] ?? 0,
    industry: IndustryType.INDUSTRY_TYPE_UNSPECIFIED,
    notes: "",
  }

  updateLocalStorage(newAsset)

  return { asset: newAsset, error: undefined, csvAsset }
}

const findAssetInLocalStorage = (csvAsset: AssetCSVRow) => {
  const localStorageAssets = getLocalStorageAssets()

  if (csvAsset.Address || !localStorageAssets) return

  const groups = getGroups(csvAsset)
  const existingGroupNames = sortGroupNames(groups)
  return localStorageAssets.find((a: AssetInput) => {
    const namesMatch = a.name === csvAsset.Name
    const groupNamesMatch = existingGroupNames.localeCompare(
      sortGroupNames(a.asset_groups ?? [])
    )
    const assetTypesMatch = csvAsset.Type === a.asset_types?.[0]
    const threatRadiiMatch = a.threat_radius === csvAsset["Threat Radius"]

    if (!csvAsset.Longitude || !csvAsset.Latitude) {
      throw new Error(
        "Latitude and Longitude required if Address is not provided"
      )
    }

    const centroidsMatch =
      a.base_event?.geotags[0]?.wkt_centroid ===
      `POINT (${csvAsset.Longitude} ${csvAsset.Latitude})`

    return (
      namesMatch &&
      groupNamesMatch &&
      assetTypesMatch &&
      threatRadiiMatch &&
      centroidsMatch
    )
  })
}

const updateLocalStorage = (newAsset: AssetInput) => {
  const localStorageAssets2 = window.localStorage.getItem(
    LOCAL_STORAGE_PREPARED_ASSETS_KEY
  )
  const assets = localStorageAssets2
    ? (JSON.parse(localStorageAssets2) as AssetInput[])
    : []
  const assetIdx = assets.findIndex((a: AssetInput) => a.name === newAsset.name)
  if (assetIdx > -1) {
    assets[assetIdx] = newAsset
  } else {
    assets.push(newAsset)
  }
  window.localStorage.setItem(
    LOCAL_STORAGE_PREPARED_ASSETS_KEY,
    JSON.stringify(assets)
  )
}

const getLocalStorageAssets = () => {
  const localStorageAssets = window.localStorage.getItem(
    LOCAL_STORAGE_PREPARED_ASSETS_KEY
  )
  return localStorageAssets
    ? (JSON.parse(localStorageAssets) as AssetInput[])
    : undefined
}

const reverseGeocodeToPHCLocation = async (
  asset: AssetCSVRow
): Promise<Geotag | undefined> => {
  if (!(asset.Longitude && asset.Latitude) && !asset.Address) return undefined

  const isAddress = asset.Address
  const query = asset.Address
    ? asset.Address
    : `${asset.Longitude || ""},${asset.Latitude || ""}`

  const result = await getMapboxGeocodeResult({
    query,
    accessToken: MAPBOX_ACCESS_TOKEN,
    types: "address,country,region,district,place",
  })

  if (result?.features.length === 0 || !result?.features[0]) {
    throw new Error(`No matching location found for ${query}`)
  }
  const [feature] = await getIdsFromGeocoderResult(
    { result: result.features[0] },
    MAPBOX_ACCESS_TOKEN
  )

  if (!feature) {
    throw new Error(`No matching location feature found for ${query}`)
  }

  const location = convertGeocoderResultToLocation({
    result: result.features[0],
  })

  location.address = ""

  const updatedCentroid =
    asset.Longitude && asset.Latitude
      ? `POINT (${asset.Longitude} ${asset.Latitude})`
      : feature.properties.centroid

  return {
    location_id: feature.properties.location_code ?? "",
    wkt_centroid: isAddress ? feature.properties.centroid : updatedCentroid,
    mapbox_location: location,
  }
}

const getGroups = (asset: AssetCSVRow): AssetGroup[] => {
  return asset.Groups.concat(BULK_ASSET_GROUP)
    .map(g => ({ asset_group_id: "", name: g }))
    .filter((group, idx, arr) => {
      return arr.findIndex(g => g.name === group.name) === idx
    })
}

const sortGroupNames = (groups: AssetGroup[]) => {
  return groups
    .map(g => g.name)
    .sort()
    .join("")
}
